import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  approveStudio,
  getStudioApprovalDetail,
  rejectStudio,
  type AdminStudioApprovalDetail,
} from '../../api/admin';
import { ApiError } from '../../api/client';
import { useToast } from '../../components/ui/ToastProvider';

const APPROVAL_STATUS_LABELS: Record<string, string> = {
  NONE: '미신청',
  PENDING: '심사 대기',
  APPROVED: '승인 완료',
  REJECTED: '반려',
};

const PARKING_TYPE_LABELS: Record<string, string> = {
  NONE: '불가',
  FREE: '무료',
  PAID: '유료',
};

const DAY_ORDER = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

const DAY_LABELS: Record<string, string> = {
  MONDAY: '월',
  TUESDAY: '화',
  WEDNESDAY: '수',
  THURSDAY: '목',
  FRIDAY: '금',
  SATURDAY: '토',
  SUNDAY: '일',
};

const REJECT_REASON_MAX = 500;

function approvalBadgeClass(status: string) {
  if (status === 'APPROVED') return 'partner-badge partner-badge--green';
  if (status === 'PENDING') return 'partner-badge partner-badge--yellow';
  if (status === 'REJECTED') return 'partner-badge partner-badge--red';
  return 'partner-badge';
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  const hour = String(parsed.getHours()).padStart(2, '0');
  const minute = String(parsed.getMinutes()).padStart(2, '0');
  return `${parsed.getFullYear()}.${month}.${day} ${hour}:${minute}`;
}

function errText(error: unknown, fallback: string) {
  if (error instanceof ApiError && error.message) return error.message;
  return fallback;
}

export function AdminStudioApprovalDetailPage() {
  const { studioId } = useParams<{ studioId: string }>();
  const { showToast } = useToast();

  const [detail, setDetail] = useState<AdminStudioApprovalDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [acting, setActing] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const reload = useCallback(async () => {
    if (!studioId) return;
    try {
      const data = await getStudioApprovalDetail(studioId);
      setDetail(data);
      setError('');
    } catch (err) {
      setDetail(null);
      setError(errText(err, '심사 정보를 불러오지 못했습니다.'));
    }
  }, [studioId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    reload().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const handleApprove = async () => {
    if (!studioId || !detail || acting) return;
    const confirmed = window.confirm(
      `'${detail.studio.name}' 업체를 승인할까요?\n점주 계정에 OWNER 권한이 부여되며 되돌릴 수 없습니다.`,
    );
    if (!confirmed) return;
    setActing(true);
    try {
      await approveStudio(studioId);
      showToast('업체를 승인했습니다. 점주에게 OWNER 권한이 부여되었습니다.');
      await reload();
    } catch (err) {
      showToast(errText(err, '승인 처리에 실패했습니다.'));
      if (err instanceof ApiError && err.status === 409) {
        await reload();
      }
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    const reason = rejectReason.trim();
    if (!studioId || acting || !reason) return;
    setActing(true);
    try {
      await rejectStudio(studioId, reason);
      showToast('업체 등록을 반려했습니다. 사유가 점주에게 전달됩니다.');
      setRejectOpen(false);
      setRejectReason('');
      await reload();
    } catch (err) {
      showToast(errText(err, '반려 처리에 실패했습니다.'));
      if (err instanceof ApiError && err.status === 409) {
        await reload();
      }
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return <p className="partner-empty">불러오는 중…</p>;
  }

  if (error || !detail) {
    return (
      <>
        <div className="partner-error">{error || '심사 정보를 불러오지 못했습니다.'}</div>
        <Link className="partner-btn partner-btn--ghost" to="/admin">
          심사 목록으로
        </Link>
      </>
    );
  }

  const { studio } = detail;
  const status = String(detail.approvalStatus);
  const isPending = status === 'PENDING';
  const sortedHours = [...studio.businessHours].sort(
    (a, b) => DAY_ORDER.indexOf(a.dayOfWeek) - DAY_ORDER.indexOf(b.dayOfWeek),
  );
  const sortedGallery = [...studio.gallery].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <>
      <div className="partner-toolbar">
        <Link className="partner-btn partner-btn--ghost partner-btn--sm" to="/admin">
          ← 심사 목록
        </Link>
      </div>

      <h1 className="partner-title">
        {studio.name}{' '}
        <span className={approvalBadgeClass(status)}>
          {APPROVAL_STATUS_LABELS[status] ?? status}
        </span>
      </h1>
      <p className="partner-subtitle">업체 등록 신청 상세 심사</p>

      <div className="partner-card">
        <h2 className="partner-card__title">심사 정보</h2>
        <dl className="partner-detail-grid">
          <div>
            <dt>제출일</dt>
            <dd>{formatDateTime(detail.submittedAt)}</dd>
          </div>
          <div>
            <dt>결정일</dt>
            <dd>{formatDateTime(detail.decidedAt)}</dd>
          </div>
          <div>
            <dt>점주</dt>
            <dd className="admin-owner-line">
              {detail.ownerNickname ?? '—'}
              {detail.ownerEmail ? ` (${detail.ownerEmail})` : ''}
            </dd>
          </div>
          <div>
            <dt>점주 ID</dt>
            <dd className="admin-cell-sub">{detail.ownerUserId}</dd>
          </div>
        </dl>

        {status === 'REJECTED' && detail.rejectedReason ? (
          <div className="admin-rejected-reason">반려 사유: {detail.rejectedReason}</div>
        ) : null}

        {isPending ? (
          <>
            <div className="admin-decision-actions">
              <button
                className="partner-btn"
                disabled={acting}
                onClick={handleApprove}
                type="button"
              >
                승인하기
              </button>
              <button
                className="partner-btn partner-btn--danger"
                disabled={acting}
                onClick={() => setRejectOpen((open) => !open)}
                type="button"
              >
                반려하기
              </button>
            </div>
            {rejectOpen ? (
              <div className="admin-reject-box">
                <label className="admin-reject-box__label" htmlFor="admin-reject-reason">
                  반려 사유 (점주에게 그대로 전달됩니다)
                </label>
                <textarea
                  id="admin-reject-reason"
                  maxLength={REJECT_REASON_MAX}
                  onChange={(event) => setRejectReason(event.target.value)}
                  placeholder="예) 사업자등록증이 흐릿합니다. 재촬영 후 다시 제출해주세요."
                  value={rejectReason}
                />
                <div className="admin-reject-box__meta">
                  <span className="admin-reject-box__count">
                    {rejectReason.length}/{REJECT_REASON_MAX}
                  </span>
                  <div className="admin-reject-box__actions">
                    <button
                      className="partner-btn partner-btn--ghost partner-btn--sm"
                      disabled={acting}
                      onClick={() => {
                        setRejectOpen(false);
                        setRejectReason('');
                      }}
                      type="button"
                    >
                      취소
                    </button>
                    <button
                      className="partner-btn partner-btn--danger partner-btn--sm"
                      disabled={acting || !rejectReason.trim()}
                      onClick={handleReject}
                      type="button"
                    >
                      반려 확정
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      <div className="partner-card">
        <h2 className="partner-card__title">사업자등록증</h2>
        {studio.businessLicenseImageUrl ? (
          <>
            <a
              className="admin-license"
              href={studio.businessLicenseImageUrl}
              rel="noreferrer"
              target="_blank"
            >
              <img alt="사업자등록증" src={studio.businessLicenseImageUrl} />
            </a>
            <p className="admin-license-hint">이미지를 클릭하면 원본을 새 창에서 확인합니다.</p>
          </>
        ) : (
          <div className="admin-license-missing">등록된 사업자등록증이 없습니다.</div>
        )}
      </div>

      <div className="partner-card">
        <h2 className="partner-card__title">업체 정보</h2>
        <dl className="partner-detail-grid">
          <div>
            <dt>소개</dt>
            <dd>{studio.description ?? '—'}</dd>
          </div>
          <div>
            <dt>연락처</dt>
            <dd>{studio.contactPhone ?? '—'}</dd>
          </div>
          <div>
            <dt>주소</dt>
            <dd>
              {studio.address?.roadAddress ?? '—'}
              {studio.address?.detailAddress ? ` ${studio.address.detailAddress}` : ''}
            </dd>
          </div>
          <div>
            <dt>주차</dt>
            <dd>
              {studio.parkingPolicy
                ? [
                    PARKING_TYPE_LABELS[studio.parkingPolicy.parkingType] ??
                      studio.parkingPolicy.parkingType,
                    studio.parkingPolicy.parkingMethod,
                    studio.parkingPolicy.parkingFeeDescription,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : '—'}
            </dd>
          </div>
          <div>
            <dt>홈페이지</dt>
            <dd>
              {studio.homepageUrls.length > 0
                ? studio.homepageUrls.map((url) => (
                    <a href={url} key={url} rel="noreferrer" target="_blank">
                      {url}
                    </a>
                  ))
                : '—'}
            </dd>
          </div>
          <div>
            <dt>휴무 안내</dt>
            <dd>{studio.holidayNote ?? '—'}</dd>
          </div>
        </dl>
      </div>

      <div className="partner-card">
        <h2 className="partner-card__title">운영시간</h2>
        {sortedHours.length === 0 ? (
          <p className="partner-empty">등록된 운영시간이 없습니다.</p>
        ) : (
          <div className="partner-hours-grid">
            {sortedHours.map((hour) => (
              <div className="partner-hours-row" key={hour.dayOfWeek}>
                <span className="partner-hours-row__day">
                  {DAY_LABELS[hour.dayOfWeek] ?? hour.dayOfWeek}
                </span>
                {hour.closed ? (
                  <span>휴무</span>
                ) : (
                  <span>
                    {hour.openTime ?? '—'} ~ {hour.closeTime ?? '—'}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="partner-card">
        <h2 className="partner-card__title">키워드</h2>
        {studio.keywords.length === 0 ? (
          <p className="partner-empty">등록된 키워드가 없습니다.</p>
        ) : (
          <div className="partner-inline-list">
            {studio.keywords.map((keyword) => (
              <span
                className="partner-chip"
                key={`${keyword.primaryCategory}-${keyword.secondaryKeyword}`}
              >
                {keyword.primaryCategory} · {keyword.secondaryKeyword}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="partner-card">
        <h2 className="partner-card__title">이미지</h2>
        {!studio.primaryImageUrl && sortedGallery.length === 0 ? (
          <p className="partner-empty">등록된 이미지가 없습니다.</p>
        ) : (
          <div className="partner-gallery">
            {studio.primaryImageUrl ? (
              <div className="partner-gallery__item">
                <img alt="대표 이미지" src={studio.primaryImageUrl} />
                <span className="partner-gallery__badge">대표</span>
              </div>
            ) : null}
            {sortedGallery.map((image) =>
              image.imageUrl ? (
                <div className="partner-gallery__item" key={image.galleryImageId}>
                  <img alt="갤러리 이미지" src={image.imageUrl} />
                </div>
              ) : null,
            )}
          </div>
        )}
      </div>
    </>
  );
}
